/**
 * Utilitário para validação de CNPJ
 * Valida formato e dígitos verificadores
 */

/**
 * Remove formatação do CNPJ (remove pontos, barras e hífens)
 */
export function cleanCNPJ(cnpj: string): string {
  return cnpj.replace(/[^\d]/g, '');
}

/**
 * Valida se o CNPJ tem 14 dígitos
 */
export function isValidCNPJLength(cnpj: string): boolean {
  const cleaned = cleanCNPJ(cnpj);
  return cleaned.length === 14;
}

/**
 * Valida se todos os dígitos são iguais (CNPJ inválido)
 */
export function isRepeatedDigits(cnpj: string): boolean {
  const cleaned = cleanCNPJ(cnpj);
  return /^(\d)\1+$/.test(cleaned);
}

/**
 * Calcula o dígito verificador do CNPJ
 */
function calculateDigit(cnpj: string, position: number): number {
  const weights = position === 12 
    ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < weights.length; i++) {
    sum += parseInt(cnpj[i]) * weights[i];
  }
  
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

/**
 * Valida o CNPJ completo (formato e dígitos verificadores)
 */
export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cleanCNPJ(cnpj);
  
  // Verificar tamanho
  if (cleaned.length !== 14) {
    return false;
  }
  
  // Verificar se todos os dígitos são iguais
  if (isRepeatedDigits(cleaned)) {
    return false;
  }
  
  // Validar primeiro dígito verificador
  const firstDigit = calculateDigit(cleaned, 12);
  if (parseInt(cleaned[12]) !== firstDigit) {
    return false;
  }
  
  // Validar segundo dígito verificador
  const secondDigit = calculateDigit(cleaned, 13);
  if (parseInt(cleaned[13]) !== secondDigit) {
    return false;
  }
  
  return true;
}

/**
 * Formata CNPJ para exibição (00.000.000/0000-00)
 */
export function formatCNPJ(cnpj: string): string {
  const cleaned = cleanCNPJ(cnpj);
  if (cleaned.length !== 14) return cnpj;
  
  return cleaned
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

